import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { adminAPI } from '../services/api';
import {
  AnalyticsContainer,
  AnalyticsHeader,
  AnalyticsTitle,
  AnalyticsSubtitle,
  FilterContainer,
  FilterGrid,
  FilterGroup,
  FilterLabel,
  FilterInput,
  FilterSelect,
  FilterButton,
  TableCard,
  TableHeader,
  TableTitle,
  TableDescription,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableCell,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  LoadingSubtext,
  ErrorContainer,
  ErrorIcon,
  ErrorTitle,
  ErrorMessage,
  RetryButton
} from '../styles/Analytics.styles';

// Custom styled components for the new Analytics
const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }

  @media (min-width: 1024px) {
    max-width: 1400px;
  }
`;

const PageHeader = styled.header`
  margin-bottom: 2rem;
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 2rem;
  }

  @media (min-width: 1024px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
`;

const CardDescription = styled.p`
  color: #64748b;
  font-size: 0.875rem;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.variant === 'outline' ? 'white' : '#2563eb'};
  color: ${props => props.variant === 'outline' ? '#374151' : 'white'};
  border: ${props => props.variant === 'outline' ? '1px solid #e2e8f0' : 'none'};
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: ${props => props.size === 'sm' ? '0.8rem' : '0.9rem'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'outline' ? '#f8fafc' : '#1d4ed8'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.status) {
      case 'active':
      case 'Active':
      case 'success':
      case 'Success':
        return 'rgba(16, 185, 129, 0.1)';
      case 'money_out':
      case 'Money Out':
      case 'withdrawal':
      case 'Withdrawal':
        return 'rgba(239, 68, 68, 0.1)';
      case 'winner':
      case 'Winner':
        return 'rgba(16, 185, 129, 0.1)';
      case 'outbid':
      case 'Outbid':
      case 'pending':
      case 'Pending':
        return 'rgba(245, 158, 11, 0.1)';
      case 'failed':
      case 'Failed':
        return 'rgba(239, 68, 68, 0.1)';
      default:
        return 'rgba(100, 116, 139, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active':
      case 'Active':
      case 'success':
      case 'Success':
        return '#10b981';
      case 'money_out':
      case 'Money Out':
      case 'withdrawal':
      case 'Withdrawal':
        return '#ef4444';
      case 'winner':
      case 'Winner':
        return '#10b981';
      case 'outbid':
      case 'Outbid':
      case 'pending':
      case 'Pending':
        return '#f59e0b';
      case 'failed':
      case 'Failed':
        return '#ef4444';
      default:
        return '#64748b';
    }
  }};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FilterContainerCustom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: end;
    justify-content: space-between;
  }
`;

const FilterGroupCustom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;

  @media (min-width: 640px) {
    gap: 0.75rem;
  }
`;

const FilterLabelCustom = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const FilterInputCustom = styled.input`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelectCustom = styled.select`
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  text-align: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.75rem;
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
`;

const StatSubtitle = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;

  .icon {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    opacity: 0.5;
  }

  h3 {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    color: #111827;
  }

  p {
    font-size: 0.9rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const LoadingSpinnerCustom = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const DateRangeInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #0369a1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

/**
 * Analytics Dashboard Component
 * 
 * Features:
 * - Date and time filtering for all analytics sections
 * - If only time is provided without dates, today's date is used as default
 * - Supports filtering by specific time ranges (e.g., "18:00 to 22:00")
 * - Real-time updates when filters change
 * - Comprehensive transaction and performance analytics
 */
function Analytics() {
  // State for shortcode stats
  const [shortcodeStats, setShortcodeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Time filter state
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  
  // USSD code selection state
  const [selectedUssdCode, setSelectedUssdCode] = useState('');
  const [ussdTransactions, setUssdTransactions] = useState([]);
  const [ussdDetails, setUssdDetails] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  
  // USSD transaction date and time filters
  const [ussdStartDate, setUssdStartDate] = useState('');
  const [ussdEndDate, setUssdEndDate] = useState('');
  const [ussdStartTime, setUssdStartTime] = useState('');
  const [ussdEndTime, setUssdEndTime] = useState('');
  
  // Auction analytics state
  const [auctionStats, setAuctionStats] = useState(null);
  const [auctionStatus, setAuctionStatus] = useState('active');
  const [auctionStartDate, setAuctionStartDate] = useState('');
  const [auctionEndDate, setAuctionEndDate] = useState('');
  const [auctionStartTime, setAuctionStartTime] = useState('');
  const [auctionEndTime, setAuctionEndTime] = useState('');
  const [loadingAuctions, setLoadingAuctions] = useState(false);
  
  // Radio show performance state
  const [radioShowPerformance, setRadioShowPerformance] = useState(null);
  const [loadingRadioShows, setLoadingRadioShows] = useState(false);
  const [radioShowStartDate, setRadioShowStartDate] = useState('');
  const [radioShowEndDate, setRadioShowEndDate] = useState('');
  const [radioShowStartTime, setRadioShowStartTime] = useState('');
  const [radioShowEndTime, setRadioShowEndTime] = useState('');
  
  // Pagination state for all tables
  const [currentPage, setCurrentPage] = useState(1);
  const [auctionCurrentPage, setAuctionCurrentPage] = useState(1);
  const [radioShowCurrentPage, setRadioShowCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Load shortcode stats on component mount
  useEffect(() => {
    loadShortcodeStats();
    loadAuctionStats();
    loadRadioShowPerformance();
  }, []);

  // Load shortcode stats with date and time filters
  const loadShortcodeStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminAPI.getShortcodeStatsWithTime(startDate, endDate, startTime, endTime);
      setShortcodeStats(response.data);
    } catch (err) {
      console.error('Error loading shortcode stats:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Load transactions for selected USSD code
  const loadUssdTransactions = async (ussdCode) => {
    if (!ussdCode) {
      setUssdTransactions([]);
      setUssdDetails(null);
      return;
    }

    try {
      setLoadingTransactions(true);
      const response = await adminAPI.getShortcodeDetails(ussdCode, ussdStartDate, ussdEndDate, ussdStartTime, ussdEndTime);
      
      if (response.data) {
        setUssdDetails(response.data);
        
        if (response.data.transactions) {
          // Transform the API response to match our table structure
          const transformedTransactions = response.data.transactions.map(transaction => ({
            id: transaction.id,
            customer_name: transaction.name,
            phone_number: transaction.msisdn,
            bid_amount: transaction.type === 'deposit' ? transaction.amount : 0,
            money_in: transaction.type === 'deposit' ? transaction.amount : 0,
            money_out: transaction.type === 'withdrawal' ? transaction.amount : 0,
            timestamp: transaction.date_created,
            status: transaction.status,
            bid_count: 1, // Default to 1 for each transaction
            transaction_type: transaction.type === 'deposit' ? 'bid' : 'money_out',
            transaction_id: transaction.transaction_id,
            reference: transaction.reference
          }));
          
          setUssdTransactions(transformedTransactions);
        } else {
          setUssdTransactions([]);
        }
      } else {
        setUssdDetails(null);
        setUssdTransactions([]);
      }
    } catch (err) {
      console.error('Error loading USSD transactions:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Handle USSD code selection and date/time changes
  useEffect(() => {
    loadUssdTransactions(selectedUssdCode);
  }, [selectedUssdCode, ussdStartDate, ussdEndDate, ussdStartTime, ussdEndTime]);

  const formatCurrency = (amount) => {
    // Handle undefined, null, or NaN values
    const safeAmount = (amount || 0);
    if (isNaN(safeAmount)) {
      return 'KES 0.00';
    }
    
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(safeAmount);
  };

  const formatNumber = (number) => {
    // Handle undefined, null, or NaN values
    const safeNumber = (number || 0);
    if (isNaN(safeNumber)) {
      return '0';
    }
    
    return new Intl.NumberFormat('en-KE').format(safeNumber);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]}-${match[2]}-${match[3]}-${match[4]}`;
    }
    return phone;
  };

  const formatDuration = (hours) => {
    // Handle undefined, null, or NaN values
    if (!hours || isNaN(hours)) {
      return '0 minutes';
    }
    
    if (hours < 1) {
      return `${Math.round(hours * 60)} minutes`;
    } else if (hours < 24) {
      return `${hours.toFixed(1)} hours`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days} day${days > 1 ? 's' : ''} ${remainingHours > 0 ? `${remainingHours.toFixed(1)} hours` : ''}`;
    }
  };

  const formatErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    return 'An unexpected error occurred. Please try again.';
  };

  // Helper function to validate time format
  const validateTimeFormat = (time) => {
    if (!time) return true;
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  // Helper function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Pagination logic for all tables
  const indexOfLastTransaction = currentPage * itemsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - itemsPerPage;
  const currentTransactions = ussdTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(ussdTransactions.length / itemsPerPage);

  // Auction pagination
  const indexOfLastAuction = auctionCurrentPage * itemsPerPage;
  const indexOfFirstAuction = indexOfLastAuction - itemsPerPage;
  const currentAuctions = auctionStats?.auctions?.slice(indexOfFirstAuction, indexOfLastAuction) || [];
  const totalAuctionPages = Math.ceil((auctionStats?.auctions?.length || 0) / itemsPerPage);

  // Radio show pagination
  const indexOfLastRadioShow = radioShowCurrentPage * itemsPerPage;
  const indexOfFirstRadioShow = indexOfLastRadioShow - itemsPerPage;
  const currentRadioShows = radioShowPerformance?.radio_shows?.slice(indexOfFirstRadioShow, indexOfLastRadioShow) || [];
  const totalRadioShowPages = Math.ceil((radioShowPerformance?.radio_shows?.length || 0) / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleAuctionPageChange = (pageNumber) => {
    setAuctionCurrentPage(pageNumber);
  };

  const handleRadioShowPageChange = (pageNumber) => {
    setRadioShowCurrentPage(pageNumber);
  };

  const handleApplyFilters = () => {
    loadShortcodeStats();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    loadShortcodeStats();
  };

  const handleClearUssdFilters = () => {
    setUssdStartDate('');
    setUssdEndDate('');
    setUssdStartTime('');
    setUssdEndTime('');
    if (selectedUssdCode) {
      loadUssdTransactions(selectedUssdCode);
    }
  };

  const loadAuctionStats = async () => {
    try {
      setLoadingAuctions(true);
      // Fix: Use the opposite status for the API call
      const apiStatus = auctionStatus === 'active' ? 'inactive' : 'active';
      const response = await adminAPI.getAuctionStats(apiStatus, auctionStartDate, auctionEndDate, auctionStartTime, auctionEndTime);
      setAuctionStats(response.data);
    } catch (err) {
      console.error('Error loading auction stats:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoadingAuctions(false);
    }
  };

  const handleClearAuctionFilters = () => {
    setAuctionStartDate('');
    setAuctionEndDate('');
    setAuctionStartTime('');
    setAuctionEndTime('');
    setAuctionStatus('active');
    loadAuctionStats();
  };

  const handleClearRadioShowFilters = () => {
    setRadioShowStartDate('');
    setRadioShowEndDate('');
    setRadioShowStartTime('');
    setRadioShowEndTime('');
    loadRadioShowPerformance();
  };

  const loadRadioShowPerformance = async () => {
    try {
      setLoadingRadioShows(true);
      const response = await adminAPI.getRadioShowPerformance(radioShowStartDate, radioShowEndDate, radioShowStartTime, radioShowEndTime);
      
      if (response.data && response.data.radio_shows) {
        setRadioShowPerformance(response.data);
      } else {
        setRadioShowPerformance(null);
      }
    } catch (err) {
      console.error('Error loading radio show performance:', err);
      setError(formatErrorMessage(err));
    } finally {
      setLoadingRadioShows(false);
    }
  };

  const getDateRangeText = () => {
    let dateText = '';
    if (startDate && endDate) {
      dateText = `${startDate} to ${endDate}`;
    } else if (startDate) {
      dateText = `from ${startDate} to today`;
    } else {
      dateText = 'today only';
    }
    
    let timeText = '';
    if (startTime && endTime) {
      timeText = ` (${startTime} to ${endTime})`;
    } else if (startTime) {
      timeText = ` (from ${startTime})`;
    } else if (endTime) {
      timeText = ` (until ${endTime})`;
    }
    
    return dateText + timeText;
  };

  const getUssdDateRangeText = () => {
    let dateText = '';
    if (ussdStartDate && ussdEndDate) {
      dateText = `${ussdStartDate} to ${ussdEndDate}`;
    } else if (ussdStartDate) {
      dateText = `from ${ussdStartDate} to today`;
    } else {
      dateText = 'today only';
    }
    
    let timeText = '';
    if (ussdStartTime && ussdEndTime) {
      timeText = ` (${ussdStartTime} to ${ussdEndTime})`;
    } else if (ussdStartTime) {
      timeText = ` (from ${ussdStartTime})`;
    } else if (ussdEndTime) {
      timeText = ` (until ${ussdEndTime})`;
    }
    
    return dateText + timeText;
  };

  const getAuctionDateRangeText = () => {
    let dateText = '';
    if (auctionStartDate && auctionEndDate) {
      dateText = `${auctionStartDate} to ${auctionEndDate}`;
    } else if (auctionStartDate) {
      dateText = `from ${auctionStartDate} to today`;
    } else {
      dateText = 'today only';
    }
    
    let timeText = '';
    if (auctionStartTime && auctionEndTime) {
      timeText = ` (${auctionStartTime} to ${auctionEndTime})`;
    } else if (auctionStartTime) {
      timeText = ` (from ${auctionStartTime})`;
    } else if (auctionEndTime) {
      timeText = ` (until ${auctionEndTime})`;
    }
    
    return dateText + timeText;
  };

  const getRadioShowDateRangeText = () => {
    let dateText = '';
    if (radioShowStartDate && radioShowEndDate) {
      dateText = `${radioShowStartDate} to ${radioShowEndDate}`;
    } else if (radioShowStartDate) {
      dateText = `from ${radioShowStartDate} to today`;
    } else {
      dateText = 'today only';
    }
    
    let timeText = '';
    if (radioShowStartTime && radioShowEndTime) {
      timeText = ` (${radioShowStartTime} to ${radioShowEndTime})`;
    } else if (radioShowStartTime) {
      timeText = ` (from ${radioShowStartTime})`;
    } else if (radioShowEndTime) {
      timeText = ` (until ${radioShowEndTime})`;
    }
    
    return dateText + timeText;
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Analytics Dashboard</PageTitle>
        <PageSubtitle>Comprehensive insights into shortcode performance and transactions</PageSubtitle>
      </PageHeader>

      {/* Time Filtering Info */}
      <div style={{
        background: '#f0f9ff',
        border: '1px solid #0ea5e9',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1rem',
        fontSize: '0.9rem',
        color: '#0369a1'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
          <strong>Time Filtering Feature</strong>
        </div>
        <p style={{ margin: 0, lineHeight: '1.5' }}>
          You can now filter data by both date and time. If you provide only time filters without dates, 
          the system will use today as the default date. This allows you to analyze data for specific time 
          periods like "6:00 AM to 10:00 AM" or "6:00 PM to 10:00 PM" across different days.
        </p>
      </div>

      {/* Date and Time Range Filters */}
      <FilterContainerCustom>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          width: '100%'
        }}>
          <FilterGroupCustom>
            <FilterLabelCustom htmlFor="start-date">Start Date</FilterLabelCustom>
            <FilterInputCustom
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FilterGroupCustom>

          <FilterGroupCustom>
            <FilterLabelCustom htmlFor="end-date">End Date</FilterLabelCustom>
            <FilterInputCustom
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FilterGroupCustom>

          <FilterGroupCustom>
            <FilterLabelCustom htmlFor="start-time">Start Time</FilterLabelCustom>
            <FilterInputCustom
              id="start-time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FilterGroupCustom>

          <FilterGroupCustom>
            <FilterLabelCustom htmlFor="end-time">End Time</FilterLabelCustom>
            <FilterInputCustom
              id="end-time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FilterGroupCustom>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'end' }}>
          <Button
            onClick={handleApplyFilters}
            disabled={loading}
            style={{ padding: '0.75rem 1.5rem' }}
          >
            {loading ? '⏳ Loading...' : '🔍 Apply Filters'}
          </Button>
          
          {(startDate || endDate || startTime || endTime) && (
            <Button
              variant="outline"
              onClick={handleClearFilters}
              style={{ padding: '0.75rem 1.5rem' }}
            >
              ❌ Clear Filters
            </Button>
          )}
        </div>
      </FilterContainerCustom>

      {/* Date Range Info */}
      <DateRangeInfo>
        📅 Showing data for: <strong>{getDateRangeText()}</strong>
        {(!startDate && !endDate) && (startTime || endTime) && (
          <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>
            (Using today as default date)
          </span>
        )}
      </DateRangeInfo>

      {/* Shortcode Statistics */}
      {loading ? (
        <Card>
          <CardContent>
            <EmptyState>
              <LoadingSpinnerCustom />
              <h3>Loading Statistics...</h3>
              <p>Please wait while we fetch the shortcode statistics.</p>
            </EmptyState>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent>
            <EmptyState>
              <div className="icon">❌</div>
              <h3>Error Loading Statistics</h3>
              <p>{error}</p>
              <Button onClick={loadShortcodeStats} style={{ marginTop: '1rem' }}>
                🔄 Retry
              </Button>
            </EmptyState>
          </CardContent>
        </Card>
      ) : shortcodeStats ? (
        <>
          {/* Statistics Cards */}
          <StatsGrid>
            <StatCard>
              <StatIcon>📱</StatIcon>
              <StatTitle>Total Shortcodes</StatTitle>
              <StatValue>{formatNumber(shortcodeStats.total_shortcodes)}</StatValue>
              <StatSubtitle>Active USSD codes</StatSubtitle>
            </StatCard>

            <StatCard>
              <StatIcon>💰</StatIcon>
              <StatTitle>Money In</StatTitle>
              <StatValue>{formatCurrency(shortcodeStats.money_in)}</StatValue>
              <StatSubtitle>{formatNumber(shortcodeStats.money_in_transactions)} transactions</StatSubtitle>
            </StatCard>

            <StatCard>
              <StatIcon>💸</StatIcon>
              <StatTitle>Money Out</StatTitle>
              <StatValue>{formatCurrency(shortcodeStats.money_out)}</StatValue>
              <StatSubtitle>{formatNumber(shortcodeStats.money_out_transactions)} transactions</StatSubtitle>
            </StatCard>

            <StatCard>
              <StatIcon>📊</StatIcon>
              <StatTitle>Net Amount</StatTitle>
              <StatValue style={{ color: shortcodeStats.money_in - shortcodeStats.money_out >= 0 ? '#10b981' : '#ef4444' }}>
                {formatCurrency(shortcodeStats.money_in - shortcodeStats.money_out)}
              </StatValue>
              <StatSubtitle>Total profit/loss</StatSubtitle>
            </StatCard>
          </StatsGrid>

          {/* Shortcodes List */}
          
        </>
      ) : (
        <Card>
          <CardContent>
            <EmptyState>
              <div className="icon">📊</div>
              <h3>No Data Available</h3>
              <p>No shortcode statistics found for the selected date range.</p>
            </EmptyState>
          </CardContent>
        </Card>
      )}

      {/* USSD Code Selection and Transactions */}
      <Card style={{ marginTop: '2rem' }}>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <CardTitle>USSD Code Transactions</CardTitle>
              <CardDescription>
                {selectedUssdCode 
                  ? `Transaction data for USSD code ${selectedUssdCode}`
                  : 'Select a USSD code to view detailed transaction data'
                }
              </CardDescription>
            </div>
            
            {selectedUssdCode && (
              <Button
                onClick={() => loadUssdTransactions(selectedUssdCode)}
                disabled={loadingTransactions}
                style={{ 
                  padding: '0.75rem 1rem',
                  fontSize: '0.9rem',
                  minWidth: 'auto'
                }}
              >
                {loadingTransactions ? '🔄' : '🔄 Refresh'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* USSD Code Selection and Date/Time Filters */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <FilterGroupCustom>
                <FilterLabelCustom htmlFor="ussd-select">Select USSD Code:</FilterLabelCustom>
                <FilterSelectCustom
                  id="ussd-select"
                  value={selectedUssdCode}
                  onChange={(e) => setSelectedUssdCode(e.target.value)}
                >
                  <option value="">Choose a USSD code to view transactions...</option>
                  {shortcodeStats?.shortcodes?.map((shortcode, index) => (
                    <option key={shortcode.id || index} value={shortcode.ussd_code}>
                      {shortcode.ussd_code}
                    </option>
                  ))}
                </FilterSelectCustom>
              </FilterGroupCustom>

              <FilterGroupCustom>
                <FilterLabelCustom htmlFor="ussd-start-date">Start Date</FilterLabelCustom>
                <FilterInputCustom
                  id="ussd-start-date"
                  type="date"
                  value={ussdStartDate}
                  onChange={(e) => setUssdStartDate(e.target.value)}
                />
              </FilterGroupCustom>

              <FilterGroupCustom>
                <FilterLabelCustom htmlFor="ussd-end-date">End Date</FilterLabelCustom>
                <FilterInputCustom
                  id="ussd-end-date"
                  type="date"
                  value={ussdEndDate}
                  onChange={(e) => setUssdEndDate(e.target.value)}
                />
              </FilterGroupCustom>

              <FilterGroupCustom>
                <FilterLabelCustom htmlFor="ussd-start-time">Start Time</FilterLabelCustom>
                <FilterInputCustom
                  id="ussd-start-time"
                  type="time"
                  value={ussdStartTime}
                  onChange={(e) => setUssdStartTime(e.target.value)}
                />
              </FilterGroupCustom>

              <FilterGroupCustom>
                <FilterLabelCustom htmlFor="ussd-end-time">End Time</FilterLabelCustom>
                <FilterInputCustom
                  id="ussd-end-time"
                  type="time"
                  value={ussdEndTime}
                  onChange={(e) => setUssdEndTime(e.target.value)}
                />
              </FilterGroupCustom>
            </div>

            {/* USSD Date Range Info and Clear Filters */}
            {selectedUssdCode && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <DateRangeInfo>
                  📅 Showing transactions for USSD {selectedUssdCode}: <strong>{getUssdDateRangeText()}</strong>
                  {(!ussdStartDate && !ussdEndDate) && (ussdStartTime || ussdEndTime) && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>
                      (Using today as default date)
                    </span>
                  )}
                </DateRangeInfo>
                
                {(ussdStartDate || ussdEndDate || ussdStartTime || ussdEndTime) && (
                  <Button
                    variant="outline"
                    onClick={handleClearUssdFilters}
                    style={{ 
                      padding: '0.5rem 1rem',
                      fontSize: '0.85rem',
                      minWidth: 'auto'
                    }}
                  >
                    ❌ Clear Date Filters
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Transaction Statistics Cards */}
          <StatsGrid>
            <StatCard>
              <StatIcon>💰</StatIcon>
              <StatTitle>Money In</StatTitle>
              <StatValue>{formatCurrency(ussdDetails?.summary?.total_deposits || 0)}</StatValue>
              <StatSubtitle>{formatNumber(ussdDetails?.summary?.total_deposit_transactions || 0)} transactions</StatSubtitle>
            </StatCard>

            <StatCard>
              <StatIcon>💸</StatIcon>
              <StatTitle>Money Out</StatTitle>
              <StatValue>{formatCurrency(ussdDetails?.summary?.total_withdrawals || 0)}</StatValue>
              <StatSubtitle>{formatNumber(ussdDetails?.summary?.total_withdrawal_transactions || 0)} transactions</StatSubtitle>
            </StatCard>

            <StatCard>
              <StatIcon>📊</StatIcon>
              <StatTitle>Net Amount (GGR)</StatTitle>
              <StatValue style={{ color: (ussdDetails?.summary?.total_ggr || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                {formatCurrency(ussdDetails?.summary?.total_ggr || 0)}
              </StatValue>
              <StatSubtitle>Total profit/loss</StatSubtitle>
            </StatCard>

            <StatCard>
              <StatIcon>📈</StatIcon>
              <StatTitle>Total Transactions</StatTitle>
              <StatValue>{formatNumber(ussdDetails?.summary?.total_transactions || 0)}</StatValue>
              <StatSubtitle>All transaction types</StatSubtitle>
            </StatCard>
          </StatsGrid>

          {/* Transactions Table */}
          {selectedUssdCode ? (
            loadingTransactions ? (
              <EmptyState>
                <LoadingSpinnerCustom />
                <h3>Loading Transactions...</h3>
                <p>Please wait while we fetch transaction data for {selectedUssdCode}.</p>
              </EmptyState>
            ) : ussdTransactions.length > 0 ? (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Name</TableHeaderCell>
                      <TableHeaderCell>Phone Number</TableHeaderCell>
                      <TableHeaderCell>Transaction ID</TableHeaderCell>
                      <TableHeaderCell>Reference</TableHeaderCell>
                      <TableHeaderCell>Amount</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Time</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {currentTransactions.map(transaction => (
                      <TableRow key={transaction.id}>
                        <TableCell data-label="Name">
                          <strong>{transaction.customer_name || 'Unknown'}</strong>
                        </TableCell>
                        <TableCell data-label="Phone Number">
                          <span style={{ fontFamily: 'monospace' }}>
                            {formatPhoneNumber(transaction.phone_number)}
                          </span>
                        </TableCell>
                        <TableCell data-label="Transaction ID">
                          <span style={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            color: '#64748b'
                          }}>
                            {transaction.transaction_id || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell data-label="Reference">
                          <span style={{ 
                            fontSize: '0.85rem',
                            color: '#64748b'
                          }}>
                            {transaction.reference || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell data-label="Amount">
                          <strong style={{ 
                            color: transaction.transaction_type === 'money_out' ? '#dc2626' : '#10b981', 
                            fontSize: '1.1rem' 
                          }}>
                            {formatCurrency(transaction.transaction_type === 'money_out' ? transaction.money_out : transaction.money_in)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Type">
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            backgroundColor: transaction.transaction_type === 'money_out' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                            color: transaction.transaction_type === 'money_out' ? '#ef4444' : '#10b981'
                          }}>
                            {transaction.transaction_type === 'money_out' ? 'Withdrawal' : 'Deposit'}
                          </span>
                        </TableCell>
                        <TableCell data-label="Time">
                          {formatDate(transaction.timestamp)}
                        </TableCell>
                        <TableCell data-label="Status">
                          <StatusBadge status={transaction.status || 'pending'}>
                            {transaction.status === 'success' ? 'Success' : 
                             transaction.status === 'pending' ? 'Pending' : 
                             transaction.status === 'failed' ? 'Failed' :
                             transaction.status || 'Unknown'}
                          </StatusBadge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination Controls */}
                {ussdTransactions.length > itemsPerPage && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: '1rem', 
                    marginTop: '2rem',
                    padding: '1rem',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        ← Prev
                      </Button>
                      
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {Array.from({ length: totalPages }, (_, index) => index + 1).map(pageNumber => (
                          <Button
                            key={pageNumber}
                            variant={currentPage === pageNumber ? "primary" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNumber)}
                            style={{ 
                              padding: '0.5rem 0.5rem',
                              minWidth: '2rem',
                              fontSize: '0.8rem'
                            }}
                          >
                            {pageNumber}
                          </Button>
                        ))}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Next →
                      </Button>
                    </div>
                    
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#64748b',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      Showing {indexOfFirstTransaction + 1}-{Math.min(indexOfLastTransaction, ussdTransactions.length)} of {ussdTransactions.length} transactions
                    </div>
                  </div>
                )}
              </>
            ) : (
              <EmptyState>
                <div className="icon">📋</div>
                <h3>No Transactions Found</h3>
                <p>No transactions found for USSD code {selectedUssdCode}.</p>
              </EmptyState>
            )
          ) : (
            <EmptyState>
              <div className="icon">📱</div>
              <h3>Select a USSD Code</h3>
              <p>Choose a USSD code from the dropdown above to view its transaction data.</p>
            </EmptyState>
          )}
        </CardContent>
      </Card>

      {/* Auction Analytics Section */}
      <Card style={{ marginTop: '2rem' }}>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <CardTitle>Auction Analytics</CardTitle>
              <CardDescription>
                Comprehensive insights into auction performance and revenue
              </CardDescription>
            </div>
            
            <Button
              onClick={loadAuctionStats}
              disabled={loadingAuctions}
              style={{ 
                padding: '0.75rem 1rem',
                fontSize: '0.9rem',
                minWidth: 'auto'
              }}
            >
              {loadingAuctions ? '⏳ Loading...' : '🔄 Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Auction Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="auction-status">Auction Status:</FilterLabelCustom>
              <FilterSelectCustom
                id="auction-status"
                value={auctionStatus}
                onChange={(e) => {
                  setAuctionStatus(e.target.value);
                  loadAuctionStats();
                }}
              >
                <option value="active">Active Auctions</option>
                <option value="inactive">Inactive Auctions</option>
              </FilterSelectCustom>
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="auction-start-date">Start Date</FilterLabelCustom>
              <FilterInputCustom
                id="auction-start-date"
                type="date"
                value={auctionStartDate}
                onChange={(e) => {
                  setAuctionStartDate(e.target.value);
                  loadAuctionStats();
                }}
              />
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="auction-end-date">End Date</FilterLabelCustom>
              <FilterInputCustom
                id="auction-end-date"
                type="date"
                value={auctionEndDate}
                onChange={(e) => {
                  setAuctionEndDate(e.target.value);
                  loadAuctionStats();
                }}
              />
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="auction-start-time">Start Time</FilterLabelCustom>
              <FilterInputCustom
                id="auction-start-time"
                type="time"
                value={auctionStartTime}
                onChange={(e) => {
                  setAuctionStartTime(e.target.value);
                  loadAuctionStats();
                }}
              />
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="auction-end-time">End Time</FilterLabelCustom>
              <FilterInputCustom
                id="auction-end-time"
                type="time"
                value={auctionEndTime}
                onChange={(e) => {
                  setAuctionEndTime(e.target.value);
                  loadAuctionStats();
                }}
              />
            </FilterGroupCustom>
          </div>

          {/* Auction Date Range Info and Clear Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
            <DateRangeInfo>
              📅 Showing {auctionStatus} auctions: <strong>{getAuctionDateRangeText()}</strong>
              {(!auctionStartDate && !auctionEndDate) && (auctionStartTime || auctionEndTime) && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>
                  (Using today as default date)
                </span>
              )}
            </DateRangeInfo>
            
            {(auctionStartDate || auctionEndDate || auctionStartTime || auctionEndTime || auctionStatus !== 'active') && (
              <Button
                variant="outline"
                onClick={handleClearAuctionFilters}
                style={{ 
                  padding: '0.5rem 1rem',
                  fontSize: '0.85rem',
                  minWidth: 'auto'
                }}
              >
                ❌ Clear Filters
              </Button>
            )}
          </div>

          {/* Auction Statistics */}
          {loadingAuctions ? (
            <EmptyState>
              <LoadingSpinnerCustom />
              <h3>Loading Auction Statistics...</h3>
              <p>Please wait while we fetch the auction data.</p>
            </EmptyState>
          ) : auctionStats ? (
            <>
              {/* Auction Summary Cards */}
              <StatsGrid>
                <StatCard>
                  <StatIcon>🎯</StatIcon>
                  <StatTitle>Total Auctions</StatTitle>
                  <StatValue>{formatNumber(auctionStats.summary?.total_auctions || 0)}</StatValue>
                  <StatSubtitle>{auctionStatus} auctions</StatSubtitle>
                </StatCard>

                <StatCard>
                  <StatIcon>💰</StatIcon>
                  <StatTitle>Money In</StatTitle>
                  <StatValue>{formatCurrency(auctionStats.summary?.total_money_in || 0)}</StatValue>
                  <StatSubtitle>Total deposits</StatSubtitle>
                </StatCard>

                <StatCard>
                  <StatIcon>💸</StatIcon>
                  <StatTitle>Money Out</StatTitle>
                  <StatValue>{formatCurrency(auctionStats.summary?.total_money_out || 0)}</StatValue>
                  <StatSubtitle>Total withdrawals</StatSubtitle>
                </StatCard>

                <StatCard>
                  <StatIcon>📊</StatIcon>
                  <StatTitle>Net Earnings</StatTitle>
                  <StatValue style={{ color: (auctionStats.summary?.total_net_earnings || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                    {formatCurrency(auctionStats.summary?.total_net_earnings || 0)}
                  </StatValue>
                  <StatSubtitle>Total profit/loss</StatSubtitle>
                </StatCard>
              </StatsGrid>

              {/* Auctions Table */}
              {auctionStats.auctions && auctionStats.auctions.length > 0 ? (
                <>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Item Name</TableHeaderCell>
                        <TableHeaderCell>USSD Code</TableHeaderCell>
                        <TableHeaderCell>Radio Show</TableHeaderCell>
                        <TableHeaderCell>Item Price</TableHeaderCell>
                        <TableHeaderCell>Target</TableHeaderCell>
                        <TableHeaderCell>Money In</TableHeaderCell>
                        <TableHeaderCell>Money Out</TableHeaderCell>
                        <TableHeaderCell>Net Earnings</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                        <TableHeaderCell>Time Range</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <tbody>
                      {currentAuctions.map((auction, index) => (
                      <TableRow key={auction.id || index}>
                        <TableCell data-label="Item Name">
                          <strong>{auction.item_name || 'Unknown Item'}</strong>
                        </TableCell>
                        <TableCell data-label="USSD Code">
                          <span style={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            color: '#64748b'
                          }}>
                            {auction.ussd || 'N/A'}
                          </span>
                        </TableCell>
                        <TableCell data-label="Radio Show">
                          {auction.radio_show_name || 'Unknown Show'}
                        </TableCell>
                        <TableCell data-label="Item Price">
                          <strong style={{ color: '#2563eb' }}>
                            {formatCurrency(auction.item_price || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Target">
                          <span style={{ 
                            color: (auction.money_in || 0) >= (auction.target || 0) ? '#10b981' : '#f59e0b',
                            fontWeight: '600'
                          }}>
                            {formatCurrency(auction.target || 0)}
                          </span>
                        </TableCell>
                        <TableCell data-label="Money In">
                          <strong style={{ color: '#10b981' }}>
                            {formatCurrency(auction.money_in || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Money Out">
                          <strong style={{ color: '#dc2626' }}>
                            {formatCurrency(auction.money_out || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Net Earnings">
                          <strong style={{ 
                            color: (auction.net_earnings || 0) >= 0 ? '#10b981' : '#ef4444'
                          }}>
                            {formatCurrency(auction.net_earnings || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Status">
                          <StatusBadge status={auction.closed ? 'closed' : 'active'}>
                            {auction.closed ? 'Closed' : 'Active'}
                          </StatusBadge>
                        </TableCell>
                        <TableCell data-label="Time Range">
                          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            <div>{formatDate(auction.start_time || new Date())}</div>
                            <div>to</div>
                            <div>{formatDate(auction.stop_time || new Date())}</div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>

                {/* Auction Pagination Controls */}
                {auctionStats.auctions.length > itemsPerPage && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: '1rem', 
                    marginTop: '2rem',
                    padding: '1rem',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAuctionPageChange(auctionCurrentPage - 1)}
                        disabled={auctionCurrentPage === 1}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        ← Prev
                      </Button>
                      
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {Array.from({ length: totalAuctionPages }, (_, index) => index + 1).map(pageNumber => (
                          <Button
                            key={pageNumber}
                            variant={auctionCurrentPage === pageNumber ? "primary" : "outline"}
                            size="sm"
                            onClick={() => handleAuctionPageChange(pageNumber)}
                            style={{ 
                              padding: '0.5rem 0.5rem',
                              minWidth: '2rem',
                              fontSize: '0.8rem'
                            }}
                          >
                            {pageNumber}
                          </Button>
                        ))}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAuctionPageChange(auctionCurrentPage + 1)}
                        disabled={auctionCurrentPage === totalAuctionPages}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Next →
                      </Button>
                    </div>
                    
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#64748b',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      Showing {indexOfFirstAuction + 1}-{Math.min(indexOfLastAuction, auctionStats.auctions.length)} of {auctionStats.auctions.length} auctions
                    </div>
                  </div>
                )}
                </>
              ) : (
                <EmptyState>
                  <div className="icon">🎯</div>
                  <h3>No Auctions Found</h3>
                  <p>No {auctionStatus} auctions found for the selected criteria.</p>
                </EmptyState>
              )}
            </>
          ) : (
            <EmptyState>
              <div className="icon">❌</div>
              <h3>Error Loading Auction Data</h3>
              <p>Failed to load auction statistics. Please try again.</p>
            </EmptyState>
          )}
        </CardContent>
      </Card>

      {/* Radio Show Performance Section */}
      <Card style={{ marginTop: '2rem' }}>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <CardTitle>Radio Show Performance</CardTitle>
              <CardDescription>
                Performance metrics aggregated by radio show
              </CardDescription>
            </div>
            
            <Button
              onClick={loadRadioShowPerformance}
              disabled={loadingRadioShows}
              style={{ 
                padding: '0.75rem 1rem',
                fontSize: '0.9rem',
                minWidth: 'auto'
              }}
            >
              {loadingRadioShows ? '⏳ Loading...' : '🔄 Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Radio Show Date and Time Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="radio-show-start-date">Start Date</FilterLabelCustom>
              <FilterInputCustom
                id="radio-show-start-date"
                type="date"
                value={radioShowStartDate}
                onChange={(e) => {
                  setRadioShowStartDate(e.target.value);
                  loadRadioShowPerformance();
                }}
              />
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="radio-show-end-date">End Date</FilterLabelCustom>
              <FilterInputCustom
                id="radio-show-end-date"
                type="date"
                value={radioShowEndDate}
                onChange={(e) => {
                  setRadioShowEndDate(e.target.value);
                  loadRadioShowPerformance();
                }}
              />
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="radio-show-start-time">Start Time</FilterLabelCustom>
              <FilterInputCustom
                id="radio-show-start-time"
                type="time"
                value={radioShowStartTime}
                onChange={(e) => {
                  setRadioShowStartTime(e.target.value);
                  loadRadioShowPerformance();
                }}
              />
            </FilterGroupCustom>

            <FilterGroupCustom>
              <FilterLabelCustom htmlFor="radio-show-end-time">End Time</FilterLabelCustom>
              <FilterInputCustom
                id="radio-show-end-time"
                type="time"
                value={radioShowEndTime}
                onChange={(e) => {
                  setRadioShowEndTime(e.target.value);
                  loadRadioShowPerformance();
                }}
              />
            </FilterGroupCustom>
          </div>

          {/* Radio Show Date Range Info and Clear Filters */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
            <DateRangeInfo>
              📅 Showing radio show performance: <strong>{getRadioShowDateRangeText()}</strong>
              {(!radioShowStartDate && !radioShowEndDate) && (radioShowStartTime || radioShowEndTime) && (
                <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>
                  (Using today as default date)
                </span>
              )}
            </DateRangeInfo>
            
            {(radioShowStartDate || radioShowEndDate || radioShowStartTime || radioShowEndTime) && (
              <Button
                variant="outline"
                onClick={handleClearRadioShowFilters}
                style={{ 
                  padding: '0.5rem 1rem',
                  fontSize: '0.85rem',
                  minWidth: 'auto'
                }}
              >
                ❌ Clear Date Filters
              </Button>
            )}
          </div>

          {/* Radio Show Performance Statistics */}
          {loadingRadioShows ? (
            <EmptyState>
              <LoadingSpinnerCustom />
              <h3>Loading Radio Show Performance...</h3>
              <p>Please wait while we calculate performance metrics by radio show.</p>
            </EmptyState>
          ) : radioShowPerformance ? (
            <>
              {/* Radio Show Summary Cards */}
              <StatsGrid>
                <StatCard>
                  <StatIcon>📻</StatIcon>
                  <StatTitle>Total Radio Shows</StatTitle>
                  <StatValue>{formatNumber(radioShowPerformance.summary?.total_shows || 0)}</StatValue>
                  <StatSubtitle>Active shows</StatSubtitle>
                </StatCard>

                <StatCard>
                  <StatIcon>🎯</StatIcon>
                  <StatTitle>Total Auctions</StatTitle>
                  <StatValue>{formatNumber(radioShowPerformance.summary?.total_auctions || 0)}</StatValue>
                  <StatSubtitle>Across all shows</StatSubtitle>
                </StatCard>

                <StatCard>
                  <StatIcon>💰</StatIcon>
                  <StatTitle>Total Money In</StatTitle>
                  <StatValue>{formatCurrency(radioShowPerformance.summary?.total_money_in || 0)}</StatValue>
                  <StatSubtitle>All shows combined</StatSubtitle>
                </StatCard>

                <StatCard>
                  <StatIcon>📊</StatIcon>
                  <StatTitle>Total Net Earnings</StatTitle>
                  <StatValue style={{ color: (radioShowPerformance.summary?.total_net_earnings || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                    {formatCurrency(radioShowPerformance.summary?.total_net_earnings || 0)}
                  </StatValue>
                  <StatSubtitle>All shows combined</StatSubtitle>
                </StatCard>
              </StatsGrid>

              {/* Radio Shows Performance Table */}
              {radioShowPerformance.radio_shows && radioShowPerformance.radio_shows.length > 0 ? (
                <>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Radio Show</TableHeaderCell>
                        <TableHeaderCell>Auctions</TableHeaderCell>
                        <TableHeaderCell>Money In</TableHeaderCell>
                        <TableHeaderCell>Money Out</TableHeaderCell>
                        <TableHeaderCell>Net Earnings</TableHeaderCell>
                        <TableHeaderCell>Avg Per Auction</TableHeaderCell>
                        <TableHeaderCell>Target Achievement</TableHeaderCell>
                        <TableHeaderCell>Avg Duration</TableHeaderCell>
                        <TableHeaderCell>Transactions</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <tbody>
                      {currentRadioShows.map((show, index) => (
                      <TableRow key={show.id || index}>
                        <TableCell data-label="Radio Show">
                          <div>
                            <strong>{show.name || 'Unknown Show'}</strong>
                            {index === 0 && (
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#fef3c7',
                                color: '#d97706',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}>
                                🏆 Top Performer
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell data-label="Auctions">
                          <strong style={{ color: '#2563eb' }}>
                            {formatNumber(show.total_auctions || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Money In">
                          <strong style={{ color: '#10b981' }}>
                            {formatCurrency(show.total_money_in || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Money Out">
                          <strong style={{ color: '#dc2626' }}>
                            {formatCurrency(show.total_money_out || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Net Earnings">
                          <strong style={{ 
                            color: (show.total_net_earnings || 0) >= 0 ? '#10b981' : '#ef4444'
                          }}>
                            {formatCurrency(show.total_net_earnings || 0)}
                          </strong>
                        </TableCell>
                        <TableCell data-label="Avg Per Auction">
                          <div style={{ fontSize: '0.85rem' }}>
                            <div style={{ color: '#10b981', fontWeight: '600' }}>
                              {formatCurrency(show.average_money_in_per_auction || 0)}
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                              avg per auction
                            </div>
                          </div>
                        </TableCell>
                        <TableCell data-label="Target Achievement">
                          <span style={{ 
                            color: (show.target_achievement_rate || 0) >= 100 ? '#10b981' : 
                                   (show.target_achievement_rate || 0) >= 80 ? '#f59e0b' : '#ef4444',
                            fontWeight: '600'
                          }}>
                            {(show.target_achievement_rate || 0).toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell data-label="Avg Duration">
                          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                            {formatDuration(show.average_auction_duration)}
                          </span>
                        </TableCell>
                        <TableCell data-label="Transactions">
                          <div style={{ fontSize: '0.85rem' }}>
                            <div style={{ color: '#10b981' }}>
                              {formatNumber(show.total_deposit_transactions || 0)} deposits
                            </div>
                            <div style={{ color: '#dc2626' }}>
                              {formatNumber(show.total_withdrawal_transactions || 0)} withdrawals
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>

                {/* Radio Show Pagination Controls */}
                {radioShowPerformance.radio_shows.length > itemsPerPage && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: '1rem', 
                    marginTop: '2rem',
                    padding: '1rem',
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRadioShowPageChange(radioShowCurrentPage - 1)}
                        disabled={radioShowCurrentPage === 1}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        ← Prev
                      </Button>
                      
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {Array.from({ length: totalRadioShowPages }, (_, index) => index + 1).map(pageNumber => (
                          <Button
                            key={pageNumber}
                            variant={radioShowCurrentPage === pageNumber ? "primary" : "outline"}
                            size="sm"
                            onClick={() => handleRadioShowPageChange(pageNumber)}
                            style={{ 
                              padding: '0.5rem 0.5rem',
                              minWidth: '2rem',
                              fontSize: '0.8rem'
                            }}
                          >
                            {pageNumber}
                          </Button>
                        ))}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRadioShowPageChange(radioShowCurrentPage + 1)}
                        disabled={radioShowCurrentPage === totalRadioShowPages}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Next →
                      </Button>
                    </div>
                    
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#64748b',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      Showing {indexOfFirstRadioShow + 1}-{Math.min(indexOfLastRadioShow, radioShowPerformance.radio_shows.length)} of {radioShowPerformance.radio_shows.length} radio shows
                    </div>
                  </div>
                )}
                </>
              ) : (
                <EmptyState>
                  <div className="icon">📻</div>
                  <h3>No Radio Shows Found</h3>
                  <p>No radio show performance data available for the selected criteria.</p>
                </EmptyState>
              )}
            </>
          ) : (
            <EmptyState>
              <div className="icon">❌</div>
              <h3>Error Loading Radio Show Data</h3>
              <p>Failed to load radio show performance statistics. Please try again.</p>
            </EmptyState>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}

export default Analytics; 